import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './WomensHealth.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faVenus,
  faCalendarDays,
  faCalendarAlt,
  faCalendarCheck,
  faDroplet,
  faNotesMedical,
  faChartLine,
  faFaceSmile,
  faCapsules,
  faBookOpen,
  faLock,
  faBookMedical,
  faClipboardList,
  faClipboardCheck,
  faCircleQuestion,
  faBaby,
  faUpload,
  faGauge,
  faHeartPulse,
  faStopwatch,
  faListCheck,
  faFileLines,
  faSuitcase,
  faBandage,
  faDumbbell,
  faBrain,
  faClock,
  faUtensils,
  faPersonRunning,
  faWeightScale,
  faSpa,
  faFileMedical,
  faTemperatureHalf,
  faGraduationCap,
  faHandHoldingHeart,
} from '@fortawesome/free-solid-svg-icons';
import { Container } from 'react-bootstrap';
import BackButton from '../../components/BackButton/BackButton';

/* ------------------------------------------------------------------ */
/*  TYPES                                                              */
/* ------------------------------------------------------------------ */

interface WhFeature {
  icon: IconDefinition;
  title: string;
  description: string;
}

interface WhSubSection {
  id: string;
  title: string;
  subtitle: string;
  icon: IconDefinition;
  features: WhFeature[];
}

type WhStageId = 'puberty' | 'pregnancy' | 'postpartum' | 'menopause';

/* ------------------------------------------------------------------ */
/*  PUBERTY SUB-SECTIONS (3 sub-sections, 6 features)                  */
/* ------------------------------------------------------------------ */

const pubertySubSections: WhSubSection[] = [
  {
    id: 'cycle-tracking',
    title: 'Cycle Tracking & Predictions',
    subtitle: 'Monitor and predict your menstrual cycle with smart tools',
    icon: faCalendarAlt,
    features: [
      {
        icon: faCalendarAlt,
        title: 'Menstrual Cycle Tracker',
        description:
          'Personalized cycle tracking with predictive calendars, visual calendar view showing current and upcoming cycles with color-coded fertile windows, and smart reminders before your expected period.',
      },
      {
        icon: faDroplet,
        title: 'Flow Intensity Tracking',
        description:
          'View flow intensity trends (light, medium, heavy) across your cycles. Uses the intensity you already logged in the Menstrual Cycle Tracker — no duplicate entry needed.',
      },
    ],
  },
  {
    id: 'symptoms-mood',
    title: 'Symptoms & Mood Tracking',
    subtitle: 'Log daily symptoms, moods, and health patterns',
    icon: faFaceSmile,
    features: [
      {
        icon: faNotesMedical,
        title: 'Daily Health Log',
        description:
          'Track cramps, mood swings, headaches, bloating, fatigue, and more. Includes PMS monitoring, daily mood check-ins, energy levels, and sleep quality with trend visualization.',
      },
      {
        icon: faCapsules,
        title: 'Medicine Reminders',
        description:
          "Integration with the app's Medicine Reminder system for pain relief, supplements, and prescribed medications.",
      },
    ],
  },
  {
    id: 'education-privacy',
    title: 'Education & Privacy',
    subtitle: 'Learn about your body and control your data',
    icon: faBookMedical,
    features: [
      {
        icon: faBookOpen,
        title: 'Educational Articles',
        description:
          'Curated content on cycle phases (follicular, ovulation, luteal, menstrual), menstrual disorders, and hygiene.',
      },
      {
        icon: faLock,
        title: 'Privacy Controls',
        description:
          'Passcode protection, anonymous mode, and configurable parent/guardian access for younger users.',
      },
    ],
  },
];

/* ------------------------------------------------------------------ */
/*  PREGNANCY SUB-SECTIONS (4 sub-sections, 13 features)               */
/* ------------------------------------------------------------------ */

const pregnancySubSections: WhSubSection[] = [
  {
    id: 'preconception',
    title: 'Preconception Planning',
    subtitle: 'Prepare your body and mind for a healthy pregnancy',
    icon: faClipboardList,
    features: [
      {
        icon: faClipboardCheck,
        title: "Do's & Don'ts Guide",
        description:
          'Comprehensive preconception checklist covering nutrition, lifestyle changes, supplements, and habits to adopt or avoid.',
      },
      {
        icon: faCalendarCheck,
        title: 'Checkup Planning',
        description:
          'Pre-pregnancy health checkup scheduler with recommended tests, vaccinations, and specialist consultations.',
      },
      {
        icon: faCircleQuestion,
        title: 'Preconception FAQs',
        description:
          'Expert-reviewed answers to common questions about fertility, timing, supplements, and preparing for conception.',
      },
      {
        icon: faCalendarDays,
        title: 'Ovulation Calendar',
        description:
          'Fertile window calculator derived from your cycle data, highlighting optimal conception days.',
      },
    ],
  },
  {
    id: 'pregnancy-tracking',
    title: 'Pregnancy Tracking',
    subtitle: "Follow your baby's growth week by week",
    icon: faBaby,
    features: [
      {
        icon: faGauge,
        title: 'Weekly Pregnancy Dashboard',
        description:
          "Your personalized weekly view with due date countdown, baby's development milestones, size comparisons, 3D visualization, health tips, expert advice, and upcoming scan reminders.",
      },
      {
        icon: faChartLine,
        title: 'Fetal Movement & Weight Tracking',
        description:
          'Daily logging of fetal kicks, weight changes, and emotional states with charts and deviation alerts.',
      },
      {
        icon: faUpload,
        title: 'Upload Check-Up Reports',
        description:
          'Securely store ultrasound images, lab results, and PDFs from prenatal visits in one organized location.',
      },
    ],
  },
  {
    id: 'health-monitoring',
    title: 'Health Monitoring & Reminders',
    subtitle: 'Stay on top of your health throughout pregnancy',
    icon: faHeartPulse,
    features: [
      {
        icon: faNotesMedical,
        title: 'Symptom Tracking',
        description:
          'Daily pregnancy symptom logging with pattern analysis for nausea, fatigue, back pain, and more.',
      },
      {
        icon: faHeartPulse,
        title: 'Vitals Monitoring',
        description:
          'Track blood pressure, glucose levels, and sleep patterns throughout your pregnancy journey.',
      },
      {
        icon: faStopwatch,
        title: 'Kick Counter & Weight Tracker',
        description:
          'Built-in kick counter for fetal movement and weight tracking with visual graphs over time.',
      },
      {
        icon: faCalendarCheck,
        title: 'Appointments & Reminders',
        description:
          'Manage prenatal appointments, scan schedules, and doctor consultations. Get timely reminders for prenatal vitamins, prescribed medications, and balanced nutrition plans.',
      },
    ],
  },
  {
    id: 'delivery-prep',
    title: 'Delivery Preparation',
    subtitle: 'Get ready for the big day with confidence',
    icon: faSuitcase,
    features: [
      {
        icon: faListCheck,
        title: 'Hospital Bag Checklist',
        description:
          'Comprehensive, customizable packing checklist for mom, baby, and partner for hospital and home.',
      },
      {
        icon: faFileLines,
        title: 'Birth Plan Templates',
        description:
          'Guided birth plan creation covering pain management preferences, delivery positions, and emergency protocols.',
      },
    ],
  },
];

/* ------------------------------------------------------------------ */
/*  POSTPARTUM SUB-SECTIONS (4 sub-sections, 10 features)              */
/* ------------------------------------------------------------------ */

const postpartumSubSections: WhSubSection[] = [
  {
    id: 'physical-recovery',
    title: 'Physical Recovery',
    subtitle: 'Track and support your postpartum healing journey',
    icon: faBandage,
    features: [
      {
        icon: faHeartPulse,
        title: 'Recovery Tracking',
        description:
          'Mode-specific recovery timeline for vaginal delivery, C-section, or VBAC with daily progress logging. Includes bleeding & lochia monitoring, incision and perineal healing milestones, and care instructions.',
      },
      {
        icon: faDumbbell,
        title: 'Pelvic Floor Exercises',
        description:
          'Guided pelvic floor rehabilitation exercises with progressive difficulty and physical therapy resources.',
      },
    ],
  },
  {
    id: 'mental-wellbeing',
    title: 'Mental & Emotional Wellbeing',
    subtitle: 'Support for your emotional health after delivery',
    icon: faBrain,
    features: [
      {
        icon: faBrain,
        title: 'Postpartum Mood Monitoring',
        description:
          'Regular mood screenings for postpartum depression, anxiety, and baby blues with professional resource links.',
      },
      {
        icon: faBookOpen,
        title: 'Miscarriage & Infertility Support',
        description:
          'Information and emotional support resources for PCOS, POI, fibroids, and other reproductive health concerns.',
      },
    ],
  },
  {
    id: 'feeding-nutrition',
    title: 'Feeding & Nutrition Support',
    subtitle: 'Guidance for breastfeeding and infant nutrition',
    icon: faUtensils,
    features: [
      {
        icon: faClock,
        title: 'Nursing & Pumping Log',
        description:
          'Track breastfeeding sessions with side-switching reminders, duration logging, and feeding frequency analysis. Manage pumping sessions with reminders, milk production volume tracking, and supply trend visualization.',
      },
      {
        icon: faUtensils,
        title: 'Solid Food Timeline',
        description:
          'Age-appropriate solid food introduction guide with allergen tracking and meal planning suggestions.',
      },
    ],
  },
  {
    id: 'body-fitness',
    title: 'Body & Fitness Recovery',
    subtitle: 'Rebuild strength and wellness after childbirth',
    icon: faDumbbell,
    features: [
      {
        icon: faPersonRunning,
        title: 'Post-Pregnancy Fitness Plans',
        description:
          'Structured exercise programs designed for postpartum recovery with progressive intensity levels.',
      },
      {
        icon: faWeightScale,
        title: 'Weight Management',
        description:
          'Healthy post-pregnancy weight loss tracking with personalized goals and nutrition guidance.',
      },
      {
        icon: faSpa,
        title: 'Core Strengthening & Yoga',
        description:
          'Diastasis recti-safe core exercises and postpartum yoga routines for physical and mental recovery.',
      },
      {
        icon: faBaby,
        title: 'Baby Care Links',
        description:
          "Quick access to the Child Development section for seamless tracking of your newborn's growth and milestones.",
      },
    ],
  },
];

/* ------------------------------------------------------------------ */
/*  MENOPAUSE SUB-SECTIONS (2 sub-sections, 5 features)                */
/* ------------------------------------------------------------------ */

const menopauseSubSections: WhSubSection[] = [
  {
    id: 'tracking-wellness',
    title: 'Symptom Tracking & Wellness',
    subtitle: 'Monitor symptoms, vitals, and health during menopause transition',
    icon: faTemperatureHalf,
    features: [
      {
        icon: faTemperatureHalf,
        title: 'Symptom & Cycle Monitoring',
        description:
          'Log hot flashes, mood changes, sleep disturbances, weight gain, irregular periods, fatigue, and joint pain. Track increasingly irregular cycles and estimate menopause transition stages with trend graphs.',
      },
      {
        icon: faGauge,
        title: 'Wellness & Vitals Dashboard',
        description:
          'Comprehensive view of symptom trends, health metrics, lifestyle wellness score, blood pressure, heart rate, and sleep quality. Includes automated red flags for prolonged bleeding, severe hot flashes, and rapid weight changes requiring medical attention.',
      },
    ],
  },
  {
    id: 'lifestyle-education',
    title: 'Lifestyle, Education & Reports',
    subtitle: 'Manage treatments, learn, and consult with your doctor',
    icon: faBookMedical,
    features: [
      {
        icon: faCapsules,
        title: 'Medication & Lifestyle',
        description:
          'Medication reminders for hormone replacement therapy, calcium, vitamin D, and supplements. Plus daily water intake goals, physical activity reminders, and diet recommendations for menopause wellness.',
      },
      {
        icon: faGraduationCap,
        title: 'Weekly Educational Modules',
        description:
          'Curated weekly content on nutrition, stress management, exercise routines, sleep hygiene, and bone/cardiovascular health.',
      },
      {
        icon: faFileMedical,
        title: 'Reports & Doctor Consultation',
        description:
          'Schedule appointments, upload medical reports, and generate auto-summaries for efficient doctor consultations.',
      },
    ],
  },
];

/* ------------------------------------------------------------------ */
/*  TAB DEFINITIONS                                                    */
/* ------------------------------------------------------------------ */

const tabs = [
  { id: 'puberty' as WhStageId, label: 'Puberty & Periods', icon: faCalendarDays, color: '#f472b6', lightColor: '#fce7f3' },
  { id: 'pregnancy' as WhStageId, label: 'Pregnancy', icon: faBaby, color: '#ec4899', lightColor: '#fdf2f8' },
  { id: 'postpartum' as WhStageId, label: 'Postpartum', icon: faHandHoldingHeart, color: '#a855f7', lightColor: '#f5f3ff' },
  { id: 'menopause' as WhStageId, label: 'Menopause', icon: faHeartPulse, color: '#f59e0b', lightColor: '#fffbeb' },
];

/* ------------------------------------------------------------------ */
/*  TAB DESCRIPTIONS                                                   */
/* ------------------------------------------------------------------ */

const tabDescriptions: Record<WhStageId, { title: string; subtitle: string }> = {
  puberty: {
    title: 'Period & Menstrual Cycle Management',
    subtitle:
      'Track your menstrual cycle, log symptoms and moods, get smart predictions, and access educational resources for better period health.',
  },
  pregnancy: {
    title: 'Pregnancy Journey Companion',
    subtitle:
      "From preconception planning through delivery — track your baby's development, manage appointments, monitor health metrics, and prepare for parenthood.",
  },
  postpartum: {
    title: 'Postpartum Recovery & Care',
    subtitle:
      'Navigate your recovery with tracking tools for healing, mood monitoring, feeding support, and body fitness — tailored to your delivery type.',
  },
  menopause: {
    title: 'Menopause Wellness Hub',
    subtitle:
      'Manage the transition with symptom tracking, hormonal monitoring, lifestyle guidance, risk assessments, and personalized wellness dashboards.',
  },
};

/* ------------------------------------------------------------------ */
/*  STATS                                                              */
/* ------------------------------------------------------------------ */

const stats = [
  { icon: faCalendarDays, color: '#f472b6', value: 'Cycle', label: 'Period Tracker' },
  { icon: faBaby, color: '#ec4899', value: '40 Weeks', label: 'Pregnancy Journey' },
  { icon: faHandHoldingHeart, color: '#a855f7', value: 'Recovery', label: 'Postpartum Care' },
  { icon: faHeartPulse, color: '#f59e0b', value: 'Wellness', label: 'Menopause Support' },
];

/* ------------------------------------------------------------------ */
/*  TAB -> SUB-SECTIONS MAP                                            */
/* ------------------------------------------------------------------ */

const tabSubSectionsMap: Record<WhStageId, WhSubSection[]> = {
  puberty: pubertySubSections,
  pregnancy: pregnancySubSections,
  postpartum: postpartumSubSections,
  menopause: menopauseSubSections,
};

/* ------------------------------------------------------------------ */
/*  FEATURE -> ROUTE MAP (each feature maps to a unique destination)   */
/* ------------------------------------------------------------------ */

const featureRouteMap: Record<string, string> = {
  // Puberty — CycleTracker tabs: cycles | log
  'Menstrual Cycle Tracker': '/womens-health/cycle-tracker?tab=cycles',
  'Flow Intensity Tracking': '/womens-health/cycle-tracker?tab=cycles',
  // Puberty — DailyLog (single page)
  'Daily Health Log': '/womens-health/daily-log',
  'Medicine Reminders': '/medicine-reminder',
  // Puberty — Education tabs: education | privacy
  'Educational Articles': '/womens-health/education?tab=education',
  'Privacy Controls': '/womens-health/education?tab=privacy',
  // Pregnancy — Preconception tabs: dos-donts | ovulation | checkup | faqs
  "Do's & Don'ts Guide": '/womens-health/preconception?tab=dos-donts',
  'Checkup Planning': '/womens-health/preconception?tab=checkup',
  'Preconception FAQs': '/womens-health/preconception?tab=faqs',
  'Ovulation Calendar': '/womens-health/preconception?tab=ovulation',
  // Pregnancy — Dashboard tabs: this-week | log-week | weight-history
  'Weekly Pregnancy Dashboard': '/womens-health/pregnancy?tab=this-week',
  'Fetal Movement & Weight Tracking': '/womens-health/pregnancy?tab=fetal-movement',
  // Pregnancy — Health tabs: checkups | kick-counter | symptom-tips | vitals-info
  'Symptom Tracking': '/womens-health/pregnancy-health?tab=symptom-tips',
  'Vitals Monitoring': '/womens-health/pregnancy-health?tab=vitals-info',
  'Kick Counter & Weight Tracker': '/womens-health/pregnancy-health?tab=kick-counter',
  'Appointments & Reminders': '/womens-health/pregnancy-health?tab=checkups&focus=appointments',
  // Pregnancy — Docs tabs: documents | birth-plan | hospital-bag
  'Upload Check-Up Reports': '/womens-health/pregnancy-docs?tab=documents',
  'Hospital Bag Checklist': '/womens-health/pregnancy-docs?tab=hospital-bag',
  'Birth Plan Templates': '/womens-health/pregnancy-docs?tab=birth-plan',
  // Postpartum — Recovery tabs: recovery | exercise | pelvic
  'Recovery Tracking': '/womens-health/postpartum-recovery?tab=recovery',
  'Pelvic Floor Exercises': '/womens-health/postpartum-recovery?tab=pelvic',
  // Postpartum — Mood tabs: checkin | history | resources
  'Postpartum Mood Monitoring': '/womens-health/postpartum-mood?tab=checkin',
  'Miscarriage & Infertility Support': '/womens-health/postpartum-mood?tab=resources',
  // Postpartum — Feeding tabs: log | history | solids
  'Nursing & Pumping Log': '/womens-health/feeding?tab=log',
  'Solid Food Timeline': '/womens-health/feeding?tab=solids',
  // Postpartum — Fitness tabs: workouts | plans | yoga | babycare
  'Post-Pregnancy Fitness Plans': '/womens-health/postpartum-fitness?tab=plans',
  'Weight Management': '/womens-health/postpartum-fitness?tab=workouts',
  'Core Strengthening & Yoga': '/womens-health/postpartum-fitness?tab=yoga',
  'Baby Care Links': '/womens-health/postpartum-fitness?tab=babycare',
  // Menopause — Hub tabs: symptoms | medications | wellness | education | reports
  'Symptom & Cycle Monitoring': '/womens-health/menopause?tab=symptoms',
  'Wellness & Vitals Dashboard': '/womens-health/menopause?tab=wellness',
  'Medication & Lifestyle': '/womens-health/menopause?tab=medications',
  'Weekly Educational Modules': '/womens-health/menopause?tab=education',
  'Reports & Doctor Consultation': '/womens-health/menopause?tab=reports',
};

/* ------------------------------------------------------------------ */
/*  COMPONENT                                                          */
/* ------------------------------------------------------------------ */

const WomensHealth: React.FC = () => {
  const { t } = useTranslation(['womensHealth', 'common']);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<WhStageId>(
    (searchParams.get('stage') as WhStageId) || 'puberty'
  );

  // In-app toast notification (replaces browser alert)
  const [toastMsg, setToastMsg] = useState<{ message: string; type: 'info' | 'success' } | null>(null);

  const handleTabChange = (tabId: WhStageId) => {
    setActiveTab(tabId);
    setSearchParams({ stage: tabId }, { replace: true });
  };

  /* --- Render a single sub-section: header + feature cards grid --- */
  const renderSubSection = (
    subSection: WhSubSection,
    accentColor: string,
    lightColor: string,
  ) => (
    <div className="wh-subsection" key={subSection.id}>
      {/* Sub-section header */}
      <div className="wh-subsection-header">
        <div
          className="wh-subsection-icon"
          style={{ background: `linear-gradient(135deg, ${lightColor}, ${accentColor}15)`, color: accentColor }}
        >
          <FontAwesomeIcon icon={subSection.icon} />
        </div>
        <div className="wh-subsection-text">
          <h3 className="wh-subsection-title" style={{ color: accentColor }}>
            {subSection.title}
          </h3>
          <p className="wh-subsection-subtitle">{subSection.subtitle}</p>
        </div>
      </div>

      {/* Feature cards grid */}
      <div className="wh-features-grid">
        {subSection.features.map((feature, idx) => (
          <div
            className="wh-feature-card"
            key={idx}
            style={{ borderTop: `4px solid ${accentColor}` }}
          >
            <div
              className="wh-feature-icon"
              style={{ background: `linear-gradient(135deg, ${lightColor}, ${accentColor}15)` }}
            >
              <FontAwesomeIcon icon={feature.icon} style={{ color: accentColor }} />
            </div>
            <h3 className="wh-feature-title">{feature.title}</h3>
            <p className="wh-feature-desc">{feature.description}</p>
            <button
              className="wh-explore-btn"
              style={{ background: `linear-gradient(135deg, ${accentColor}, ${accentColor}cc)` }}
              onClick={() => {
                const route = featureRouteMap[feature.title];
                if (route) {
                  navigate(route);
                  window.scrollTo(0, 0);
                } else {
                  setToastMsg({ message: `"${feature.title}" -- this feature is coming soon!`, type: 'info' });
                  setTimeout(() => setToastMsg(null), 4000);
                }
              }}
            >
              {t('common:buttons.view')}
            </button>
          </div>
        ))}
      </div>
    </div>
  );

  /* --- Derived data for active tab --- */
  const activeTabData = tabs.find((t) => t.id === activeTab)!;
  const activeDesc = tabDescriptions[activeTab];
  const activeSubSections = tabSubSectionsMap[activeTab];

  /* --- Main render --- */
  return (
    <div className="womens-health-page">
      {/* Welcome Banner */}
      <section className="wh-welcome-banner">
        <Container>
          <BackButton />
          <div className="wh-welcome-layout">
            <div className="wh-welcome-text">
              <h1 className="wh-welcome-title">
                <FontAwesomeIcon icon={faVenus} />
                {t('womensHealth:title')}
              </h1>
              <p className="wh-welcome-subtitle">
                Comprehensive care through every stage of womanhood &mdash; from puberty and
                periods to pregnancy, postpartum recovery, and menopause wellness.
              </p>
            </div>
          </div>
        </Container>
      </section>

      {/* Stats Row */}
      <section className="wh-stats-section">
        <Container>
          <div className="wh-stats-row">
            {stats.map((stat, idx) => (
              <div className="wh-stat-card" key={idx}>
                <div
                  className="wh-stat-icon"
                  style={{ background: `${stat.color}12`, color: stat.color }}
                >
                  <FontAwesomeIcon icon={stat.icon} />
                </div>
                <div className="wh-stat-info">
                  <span className="wh-stat-value">{stat.value}</span>
                  <span className="wh-stat-label">{stat.label}</span>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Tab Navigation */}
      <Container>
        <div className="wh-tabs-wrapper">
          <div className="wh-tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`wh-tab-btn${activeTab === tab.id ? ' active' : ''}`}
                onClick={() => handleTabChange(tab.id)}
                style={
                  activeTab === tab.id
                    ? { background: tab.color, borderColor: 'transparent' }
                    : undefined
                }
              >
                <FontAwesomeIcon icon={tab.icon} className="wh-tab-icon" />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Active Tab Content */}
        <section className="wh-tab-content" key={activeTab}>
          {/* Section header banner */}
          <div
            className="wh-section-header"
            style={{
              background: `linear-gradient(135deg, ${activeTabData.lightColor}, #fff)`,
            }}
          >
            <div className="wh-section-header-text">
              <h2 className="wh-section-title" style={{ color: activeTabData.color }}>
                <FontAwesomeIcon icon={activeTabData.icon} /> {activeDesc.title}
              </h2>
              <p className="wh-section-subtitle">{activeDesc.subtitle}</p>
            </div>
          </div>

          {/* Sub-sections */}
          {activeSubSections.map((subSection) =>
            renderSubSection(subSection, activeTabData.color, activeTabData.lightColor),
          )}
        </section>
      </Container>

      {/* In-app toast notification */}
      {toastMsg && (
        <div
          style={{
            position: 'fixed',
            bottom: 32,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10000,
            padding: '12px 24px',
            borderRadius: 12,
            color: '#fff',
            fontWeight: 500,
            fontSize: 14,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
            background: toastMsg.type === 'info' ? '#3b82f6' : '#10b981',
          }}
        >
          <span>{toastMsg.message}</span>
          <button
            onClick={() => setToastMsg(null)}
            style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', marginLeft: 8, fontSize: 14 }}
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
};

export default WomensHealth;
